/**
 * Follow-ups page (PRD §11.2 + §13 + addendum Apr-2026).
 *
 * Cross-record view of every follow-up the caller can see, with two
 * presentation modes (List / Calendar) backed by the same filter
 * state. Top-level "+ New follow-up" launches the parent-aware
 * dialog (incl. Standalone follow-ups — the addendum's "calendar
 * invite" mode).
 *
 * Filters
 * =======
 * The filter bar drives both views. Server-side filters that map
 * directly onto the API (``parent_type``, ``mine_only``, ``assignee_id``,
 * ``open_only``) ride along the request. Date-range and the
 * Today/Overdue/Completed *state* facets are post-filtered in JS
 * because the API doesn't accept a date_range param and adding one
 * for v1 is more surface than we need — a few hundred rows trickle
 * out of the server, and post-filtering keeps the cache key small.
 *
 * Visibility & roles
 * ==================
 * Server enforces visibility (PRD §7.2, plus the addendum's "creator
 * + invitees + manager+ for standalones"). The page just renders
 * what comes back. Sales callers don't see the assignee picker
 * (manager-only).
 *
 * Calendar
 * ========
 * In-house lightweight grid (see ``FollowUpsCalendar``). The list
 * view is unchanged in shape but rebuilt to render the standalone
 * "Standalone" badge and the new ``title`` field. Both views read
 * from the same ``items`` so toggling tabs is instant — no refetch.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  CalendarClock,
  CalendarPlus,
  CheckCircle2,
  Loader2,
  RotateCcw,
  Trash2,
} from "lucide-react";
import { useMemo, useState } from "react";

import { SearchableUserPicker } from "../components/SearchableUserPicker";
import { CloseFollowUpDialog } from "../components/followups/CloseFollowUpDialog";
import { FollowUpsCalendar } from "../components/followups/FollowUpsCalendar";
import { NewFollowUpDialog } from "../components/followups/FollowUpDialog";
import {
  ApiError,
  type FollowUp,
  type FollowUpParentType,
  followupsApi,
  usersApi,
} from "../lib/api";
import { useAuthStore } from "../lib/auth";

type StateFilter = "open" | "today" | "overdue" | "completed" | "all";
type ScopeFilter = "mine" | "all";
type View = "list" | "calendar";
type ParentTypeFilter = "any" | FollowUpParentType;

export const Route = createFileRoute("/_app/followups/")({
  component: FollowUpsListPage,
});

function FollowUpsListPage() {
  const user = useAuthStore((s) => s.user);
  const isManagerPlus = user?.role === "admin" || user?.role === "manager";

  // ── Filter state (shared by list + calendar) ──
  const [view, setView] = useState<View>("list");
  const [stateFilter, setStateFilter] = useState<StateFilter>("open");
  // Reps default to "mine"; managers default to "all" because that's
  // the lens they actually want when they land on the page. Either
  // role can flip the tab.
  const [scope, setScope] = useState<ScopeFilter>(isManagerPlus ? "all" : "mine");
  const [assigneeId, setAssigneeId] = useState("");
  const [parentTypeFilter, setParentTypeFilter] = useState<ParentTypeFilter>("any");
  const [dateFrom, setDateFrom] = useState(""); // YYYY-MM-DD
  const [dateTo, setDateTo] = useState(""); // YYYY-MM-DD
  const [showNewDialog, setShowNewDialog] = useState(false);

  const usersQuery = useQuery({
    queryKey: ["users", { active_only: true }],
    queryFn: () => usersApi.list({ active_only: true }),
    enabled: isManagerPlus,
  });

  // Build the server-side filter set. The "completed" state filter
  // implies open_only=false; everything else asks for opens-only and
  // post-filters in JS.
  const params = useMemo(() => {
    const p: Parameters<typeof followupsApi.list>[0] = { limit: 200, offset: 0 };
    if (stateFilter === "open" || stateFilter === "today" || stateFilter === "overdue") {
      p.open_only = true;
    } else if (stateFilter === "completed") {
      p.open_only = false; // we'll post-filter to is_completed=true
    }
    if (parentTypeFilter !== "any") {
      p.parent_type = parentTypeFilter;
    }
    if (assigneeId) {
      p.assignee_id = assigneeId;
    } else if (scope === "mine") {
      p.mine_only = true;
    }
    return p;
  }, [stateFilter, parentTypeFilter, assigneeId, scope]);

  const queryClient = useQueryClient();
  const { data, error, isLoading, isFetching } = useQuery({
    queryKey: ["followups", params],
    queryFn: () => followupsApi.list(params),
  });

  const myDoneIds = useMemo(() => {
    if (!user) return new Set<string>();
    const s = new Set<string>();
    for (const f of data?.items ?? []) {
      const slice = f.assignees.find((a) => a.user_id === user.id);
      if (slice && slice.completed_at !== null) s.add(f.id);
    }
    return s;
  }, [data, user]);

  // Post-filter in JS for the facets the server doesn't natively
  // accept (state-by-day, date range).
  const items = useMemo(() => {
    let xs = data?.items ?? [];
    if (stateFilter === "today") xs = xs.filter((f) => isToday(new Date(f.scheduled_at)));
    else if (stateFilter === "overdue") xs = xs.filter((f) => f.is_overdue);
    else if (stateFilter === "completed") xs = xs.filter((f) => f.is_completed);
    if (dateFrom) {
      const from = new Date(`${dateFrom}T00:00:00`);
      xs = xs.filter((f) => new Date(f.scheduled_at) >= from);
    }
    if (dateTo) {
      const to = new Date(`${dateTo}T23:59:59.999`);
      xs = xs.filter((f) => new Date(f.scheduled_at) <= to);
    }
    return xs;
  }, [data, stateFilter, dateFrom, dateTo]);

  const onCreated = () => {
    queryClient.invalidateQueries({ queryKey: ["followups"] });
    queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    queryClient.invalidateQueries({ queryKey: ["notifications"] });
  };

  const clearFilters = () => {
    setStateFilter("open");
    setScope(isManagerPlus ? "all" : "mine");
    setAssigneeId("");
    setParentTypeFilter("any");
    setDateFrom("");
    setDateTo("");
  };

  const filtersActive =
    stateFilter !== "open" || parentTypeFilter !== "any" || !!assigneeId || !!dateFrom || !!dateTo;

  return (
    <div className="page-shell space-y-5 p-4 sm:p-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight">Follow-ups</h1>
          <p className="text-sm opacity-60">
            Scheduled actions across every record you can see — plus standalone tasks. Tap Done to
            close one out, or open a row to reschedule.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowNewDialog(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-[var(--accent)] px-3 py-2 text-sm font-medium text-white hover:opacity-90"
          >
            <CalendarPlus className="size-4" aria-hidden />
            <span className="hidden sm:inline">New follow-up</span>
            <span className="sm:hidden">New</span>
          </button>
          {user?.role === "admin" && (
            <Link
              to="/followups/trash"
              className="inline-flex items-center gap-1.5 rounded-md border border-[var(--border)] px-3 py-2 text-sm font-medium hover:bg-black/5 dark:hover:bg-white/5"
              aria-label="Trash"
            >
              <Trash2 className="size-4" aria-hidden />
              <span className="hidden sm:inline">Trash</span>
            </Link>
          )}
        </div>
      </header>

      {/* Filters — wrap freely; each FilterGroup owns its own min-width
          via flex-shrink. On phones each group stacks naturally. */}
      <div className="flex flex-wrap items-end gap-x-3 gap-y-2 rounded-xl border border-[var(--border)] p-3">
        <FilterGroup label="State">
          <Pills
            options={[
              { v: "open", label: "Open" },
              { v: "today", label: "Today" },
              { v: "overdue", label: "Overdue" },
              { v: "completed", label: "Completed" },
              { v: "all", label: "All" },
            ]}
            value={stateFilter}
            onChange={setStateFilter}
            ariaLabel="Filter by state"
          />
        </FilterGroup>

        <FilterGroup label="Type">
          <Pills
            options={[
              { v: "any", label: "Any" },
              { v: "none", label: "Standalone" },
              { v: "contact", label: "Contact" },
              { v: "account", label: "Account" },
              { v: "deal", label: "Deal" },
            ]}
            value={parentTypeFilter}
            onChange={setParentTypeFilter}
            ariaLabel="Filter by parent type"
          />
        </FilterGroup>

        <FilterGroup label="Scope">
          <Pills
            options={[
              { v: "mine", label: "Mine" },
              { v: "all", label: "All" },
            ]}
            value={assigneeId ? "all" : scope}
            onChange={(next) => {
              setScope(next);
              setAssigneeId("");
            }}
            ariaLabel="Filter by scope"
          />
        </FilterGroup>

        {isManagerPlus && (
          <FilterGroup label="Assignee">
            <SearchableUserPicker
              value={assigneeId}
              onChange={(v) => setAssigneeId(v)}
              users={usersQuery.data?.items ?? []}
              loading={usersQuery.isLoading}
              emptyLabel="Anyone"
              ariaLabel="Filter by assignee"
            />
          </FilterGroup>
        )}

        <FilterGroup label="From">
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="w-[10rem] rounded-md border border-[var(--border)] bg-transparent px-2 py-1 text-xs outline-none focus:border-[var(--accent)]"
          />
        </FilterGroup>
        <FilterGroup label="To">
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="w-[10rem] rounded-md border border-[var(--border)] bg-transparent px-2 py-1 text-xs outline-none focus:border-[var(--accent)]"
          />
        </FilterGroup>

        {filtersActive && (
          <button
            type="button"
            onClick={clearFilters}
            className="inline-flex items-center gap-1.5 rounded-md border border-[var(--border)] px-2 py-1 text-xs hover:bg-black/5 dark:hover:bg-white/5 sm:ml-auto"
          >
            <RotateCcw className="size-3" aria-hidden />
            Clear filters
          </button>
        )}
      </div>

      {/* List | Calendar tabs */}
      <div className="flex items-center justify-between gap-3">
        <div
          role="tablist"
          aria-label="View"
          className="inline-flex rounded-md border border-[var(--border)] p-0.5 text-sm"
        >
          <ViewTab active={view === "list"} onClick={() => setView("list")} label="List" />
          <ViewTab
            active={view === "calendar"}
            onClick={() => setView("calendar")}
            label="Calendar"
          />
        </div>
        <span className="text-xs opacity-60">
          {isLoading
            ? "Loading…"
            : isFetching
              ? "Refreshing…"
              : `${items.length} ${items.length === 1 ? "follow-up" : "follow-ups"}`}
        </span>
      </div>

      {/* Body */}
      {isLoading ? (
        <p className="text-sm opacity-60">Loading...</p>
      ) : error ? (
        <div
          role="alert"
          className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
        >
          Couldn't load follow-ups: {(error as Error).message}
        </div>
      ) : view === "list" ? (
        items.length === 0 ? (
          <Empty filter={stateFilter} />
        ) : (
          <ul className="divide-y divide-[var(--border)] overflow-hidden rounded-xl border border-[var(--border)]">
            {items.map((f) => (
              <FollowUpRow key={f.id} followup={f} myDone={myDoneIds.has(f.id)} />
            ))}
          </ul>
        )
      ) : (
        <FollowUpsCalendar items={items} myDoneIds={myDoneIds} />
      )}

      <NewFollowUpDialog
        open={showNewDialog}
        onClose={() => setShowNewDialog(false)}
        onCreated={onCreated}
      />
    </div>
  );
}

// ── Filter primitives ───────────────────────────────────────────────

function FilterGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <div className="text-[11px] font-medium uppercase tracking-wide opacity-60">{label}</div>
      {children}
    </div>
  );
}

function Pills<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
}: {
  options: Array<{ v: T; label: string }>;
  value: T;
  onChange: (next: T) => void;
  ariaLabel: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className="inline-flex flex-wrap rounded-md border border-[var(--border)] p-0.5 text-sm"
    >
      {options.map((opt) => (
        <button
          key={opt.v}
          type="button"
          role="tab"
          aria-selected={value === opt.v}
          onClick={() => onChange(opt.v)}
          className={`rounded px-2.5 py-1 text-xs transition-colors ${
            value === opt.v
              ? "bg-[var(--accent)] text-white"
              : "opacity-70 hover:bg-black/5 hover:opacity-100 dark:hover:bg-white/5"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function ViewTab({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`rounded px-3 py-1.5 text-sm transition-colors ${
        active
          ? "bg-[var(--accent)] text-white"
          : "opacity-70 hover:bg-black/5 hover:opacity-100 dark:hover:bg-white/5"
      }`}
    >
      {label}
    </button>
  );
}

function Empty({ filter }: { filter: StateFilter }) {
  return (
    <div className="rounded-xl border border-dashed border-[var(--border)] p-10 text-center">
      <CalendarClock className="mx-auto mb-3 size-8 opacity-40" aria-hidden />
      <p className="text-sm font-medium">
        {filter === "overdue"
          ? "Nothing overdue. Nice."
          : filter === "today"
            ? "Nothing scheduled today."
            : filter === "open"
              ? "No open follow-ups."
              : filter === "completed"
                ? "No completed follow-ups in this view."
                : "No follow-ups match these filters."}
      </p>
      <p className="mt-1 text-sm opacity-60">Use “New follow-up” above to schedule one.</p>
    </div>
  );
}

// ── List row ────────────────────────────────────────────────────────

function FollowUpRow({ followup }: { followup: FollowUp; myDone: boolean }) {
  // ``myDone`` is kept on the prop signature for backwards-compat with
  // existing call sites (calendar grid, list), but in the new
  // single-decisive close model (PRD addendum Apr-2026) it always
  // mirrors ``followup.is_completed`` — any assignee closing the row
  // stamps every slice at once. The row reads from
  // ``is_completed`` alone, so the prop is intentionally ignored.
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [closeOpen, setCloseOpen] = useState(false);

  const reopenMutation = useMutation({
    mutationFn: () => followupsApi.uncomplete(followup.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["followups"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "Could not update.";
      setError(msg);
    },
  });

  const onClosed = () => {
    queryClient.invalidateQueries({ queryKey: ["followups"] });
    queryClient.invalidateQueries({ queryKey: ["dashboard"] });
  };

  const due = new Date(followup.scheduled_at);
  const dueText = due.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

  const isLineThrough = followup.is_completed;

  return (
    <li className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <StatePill followup={followup} />
          <span
            className={
              isLineThrough
                ? "opacity-60 line-through"
                : followup.is_overdue
                  ? "font-medium text-red-700 dark:text-red-300"
                  : "font-medium"
            }
          >
            {dueText}
          </span>
        </div>
        {followup.title && (
          <p className={`text-sm font-medium ${isLineThrough ? "opacity-60" : ""}`}>
            {followup.title}
          </p>
        )}
        {followup.note && (
          <p className={`text-sm ${isLineThrough ? "opacity-60" : "opacity-90"}`}>
            {followup.note}
          </p>
        )}
        {followup.is_completed && followup.completion_summary && (
          <p className="rounded-md border border-[var(--border)] bg-emerald-50/40 px-2 py-1 text-xs italic dark:bg-emerald-950/15">
            <span className="not-italic font-medium opacity-70">Closing summary: </span>
            {followup.completion_summary}
          </p>
        )}
        <ParentLink followup={followup} />
        {error && <div className="mt-1 text-xs text-red-700 dark:text-red-300">{error}</div>}
      </div>
      <button
        type="button"
        onClick={() => {
          setError(null);
          if (followup.is_completed) {
            reopenMutation.mutate();
          } else {
            setCloseOpen(true);
          }
        }}
        disabled={reopenMutation.isPending}
        className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50 ${
          isLineThrough
            ? "border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-900 dark:text-amber-300 dark:hover:bg-amber-950/40"
            : "border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-900 dark:text-emerald-300 dark:hover:bg-emerald-950/40"
        }`}
      >
        {reopenMutation.isPending ? (
          <Loader2 className="size-4 animate-spin" aria-hidden />
        ) : isLineThrough ? (
          <RotateCcw className="size-4" aria-hidden />
        ) : (
          <CheckCircle2 className="size-4" aria-hidden />
        )}
        {isLineThrough ? "Reopen" : "Done"}
      </button>
      <CloseFollowUpDialog
        open={closeOpen}
        followup={followup}
        onClose={() => setCloseOpen(false)}
        onClosed={onClosed}
      />
    </li>
  );
}

function ParentLink({ followup }: { followup: FollowUp }) {
  // Standalone follow-ups have no parent — render a "Standalone" badge.
  if (followup.parent_type === "none" || followup.parent_id === null) {
    return (
      <span className="inline-flex items-center rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-600 dark:bg-zinc-800/60 dark:text-zinc-300">
        Standalone
      </span>
    );
  }
  // The "parent" is one of contact/account/deal — link to the right
  // detail page for each so reps land where the action is.
  const label =
    followup.parent_type === "contact"
      ? `Open ${followup.parent_summary?.name ?? "contact"} →`
      : followup.parent_type === "account"
        ? `Open ${followup.parent_summary?.name ?? "account"} →`
        : `Open ${followup.parent_summary?.name ?? "deal"} →`;
  if (followup.parent_type === "contact") {
    return (
      <Link
        to="/contacts/$contactId"
        params={{ contactId: followup.parent_id }}
        className="text-xs text-[var(--accent)] hover:underline"
      >
        {label}
      </Link>
    );
  }
  if (followup.parent_type === "account") {
    return (
      <Link
        to="/accounts/$accountId"
        params={{ accountId: followup.parent_id }}
        className="text-xs text-[var(--accent)] hover:underline"
      >
        {label}
      </Link>
    );
  }
  return (
    <Link
      to="/deals/$dealId"
      params={{ dealId: followup.parent_id }}
      className="text-xs text-[var(--accent)] hover:underline"
    >
      {label}
    </Link>
  );
}

function StatePill({ followup }: { followup: FollowUp }) {
  if (followup.is_completed) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-300">
        <CheckCircle2 className="size-3" aria-hidden /> Completed
      </span>
    );
  }
  if (followup.is_overdue) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/40 dark:text-red-300">
        Overdue
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
      Open
    </span>
  );
}

function isToday(d: Date): boolean {
  const today = new Date();
  return (
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate()
  );
}
